import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Select from "react-select";
import { Line, Bar } from "react-chartjs-2";
import AdminHeader from "./Header";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
);

/* ─────────────────────────────────────────────
   OPTIONS 
───────────────────────────────────────────── */
const DATE_OPTIONS = [
  { value: 7,  label: "Last 7 Days" },
  { value: 5,  label: "Last 5 Days" },
  { value: 3,  label: "Last 3 Days" }
];

const TOP_OPTIONS = [
  { value: 5,  label: "Top 5"  },
  { value: 10, label: "Top 10" }
];

const RECENT_OPTIONS = [
  { value: 5,  label: "Show 5"  },
  { value: 10, label: "Show 10" }
];

const selectCustomStyles = {
  control: (base) => ({
    ...base,
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "2px 6px",
    boxShadow: "none",
    backgroundColor: "#ffffff",
    fontSize: "0.875rem",
    fontWeight: "500",
    color: "#1e293b",
    minWidth: "160px",
    "&:hover": { borderColor: "#cbd5e1" }
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05)",
    border: "1px solid #f1f5f9"
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#6366f1" : state.isFocused ? "#f8fafc" : "transparent",
    color: state.isSelected ? "#ffffff" : "#475569",
    fontSize: "0.875rem",
    cursor: "pointer"
  })
};

/* ─────────────────────────────────────────────
   CHART MODERN OPTIONS
───────────────────────────────────────────── */
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { 
    legend: { 
      position: "top",
      labels: { boxWidth: 10, font: { family: "Inter, sans-serif", weight: "500" }, color: "#64748b" }
    } 
  },
  scales: {
    y: { 
      beginAtZero: true, 
      ticks: { precision: 0, color: "#94a3b8", font: { family: "Inter" } },
      grid: { color: "#f1f5f9" }
    },
    x: {
      ticks: { color: "#94a3b8", font: { family: "Inter" } },
      grid: { display: false }
    }
  }
};

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const AdminDashboard = () => {
  const dispatch = useDispatch();

  const [summary,       setSummary]       = useState({});
  const [topBusinesses, setTopBusinesses] = useState([]);
  const [recentChats,   setRecentChats]   = useState([]);
  const [recentPosts,   setRecentPosts]   = useState([]);
  const [graphs,        setGraphs]        = useState({});

  const [days,   setDays]   = useState(7);
  const [top,    setTop]    = useState(5);
  const [recent, setRecent] = useState(5);

  /* ── Derive Select objects from primitive state ── */
  const dateRange   = DATE_OPTIONS.find(o => o.value === days)   || DATE_OPTIONS[0];
  const topLimit    = TOP_OPTIONS.find(o => o.value === top)     || TOP_OPTIONS[0];
  const recentLimit = RECENT_OPTIONS.find(o => o.value === recent) || RECENT_OPTIONS[0];

  /* ── Fetch ── */
  const fetchDashboard = async (d, t, r) => {
    dispatch(loadingTrue());
    try {
      const res = await Handler({
        method: "get",
        url: `/admin/dashboard.php?days=${d}&top=${t}&recent=${r}`
      });

      if (res?.success) {
        const data = res.data;
        setSummary(data.summary             || {});
        setTopBusinesses(data.top_businesses || []);
        setRecentChats(data.recent_chats    || []);
        setRecentPosts(data.recent_posts    || []);
        setGraphs(data.graphs               || {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(loadingFalse());
    }
  };

  useEffect(() => {
    fetchDashboard(days, top, recent);
  }, [days, top, recent]);

  /* ── Graph datasets ── */
  const messagesGraph = useMemo(() => ({
    labels: (graphs.messages_per_day || []).map(i => i.day),
    datasets: [{
      label: "Messages",
      data: (graphs.messages_per_day || []).map(i => i.total),
      borderColor: "#6366f1",
      backgroundColor: "rgba(99,102,241,0.04)",
      pointBackgroundColor: "#6366f1",
      fill: true,
      tension: 0.4
    }]
  }), [graphs]);

  const businessGraph = useMemo(() => ({
    labels: (graphs.business_registrations || []).map(i => i.day),
    datasets: [{
      label: "Registrations",
      data: (graphs.business_registrations || []).map(i => i.total),
      backgroundColor: "#10b981", 
      borderRadius: 6
    }]
  }), [graphs]);

  const postsGraph = useMemo(() => ({
    labels: (graphs.posts_per_day || []).map(i => i.day),
    datasets: [{
      label: "Posts",
      data: (graphs.posts_per_day || []).map(i => i.total),
      borderColor: "#f59e0b",
      backgroundColor: "rgba(245,158,11,0.04)",
      pointBackgroundColor: "#f59e0b",
      fill: true,
      tension: 0.4
    }]
  }), [graphs]);

  /* ── Colorful Stat Cards Configuration ── */
  const statCards = [
    { title: "Businesses",        value: summary.total_businesses,            background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)", shadow: "rgba(79, 70, 229, 0.25)", icon: "🏢" },
    { title: "Active",            value: summary.active_businesses,           background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", shadow: "rgba(16, 185, 129, 0.25)", icon: "🟢" },
    { title: "Inactive",          value: summary.inactive_businesses,         background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", shadow: "rgba(239, 68, 68, 0.25)", icon: "🔴" },
    { title: "Posts",             value: summary.total_posts,                 background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)", shadow: "rgba(14, 165, 233, 0.25)", icon: "📝" },
    { title: "Chats",             value: summary.total_chats,                 background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", shadow: "rgba(245, 158, 11, 0.25)", icon: "💬" },
    { title: "Messages",          value: summary.total_messages,              background: "linear-gradient(135deg, #64748b 0%, #475569 100%)", shadow: "rgba(100, 116, 139, 0.25)", icon: "✉️" },
    { title: "Today Msgs",        value: summary.today_messages,              background: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)", shadow: "rgba(236, 72, 153, 0.25)", icon: "📊" },
    { title: "Notifications",     value: summary.admin_unread_notifications,  background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", shadow: "rgba(249, 115, 22, 0.25)", icon: "🔔" }
  ];

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      <AdminHeader />
      
      <div className="container py-5">
        
        {/* ── Control Header Panel ── */}
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-5 gap-4">
          <div>
            <h1 className="fw-bold tracking-tight text-slate-900 mb-1" style={{ fontSize: "1.75rem", color: "#0f172a" }}>
              Analytics Dashboard
            </h1>
            <p className="text-muted mb-0" style={{ fontSize: "0.925rem", color: "#64748b" }}>
              Real-time dashboard controls and metric configurations.
            </p>
          </div>
          
          <div className="d-flex flex-wrap gap-3 align-items-center bg-white p-2 rounded-4 shadow-sm border border-light">
            <Select styles={selectCustomStyles} value={dateRange} onChange={opt => setDays(opt.value)} options={DATE_OPTIONS} />
            <Select styles={selectCustomStyles} value={topLimit} onChange={opt => setTop(opt.value)} options={TOP_OPTIONS} />
            <Select styles={selectCustomStyles} value={recentLimit} onChange={opt => setRecent(opt.value)} options={RECENT_OPTIONS} />
          </div>
        </div>

        {/* ── Colorful Vibrant Metrics Grid ── */}
        <div className="row g-4 mb-5">
          {statCards.map((card, i) => (
            <div key={i} className="col-12 col-sm-6 col-md-4 col-xl-3">
              <div 
                className="card h-100 border-0 rounded-4 p-2 transition-transform" 
                style={{ 
                  background: card.background, 
                  boxShadow: `0 10px 20px -5px ${card.shadow}, 0 4px 6px -2px rgba(0,0,0,0.05)`
                }}
              >
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div>
                    <span className="d-block tracking-wider fw-semibold text-uppercase mb-1" style={{ fontSize: "0.725rem", color: "rgba(255, 255, 255, 0.8)", letterSpacing: "0.05em" }}>
                      {card.title}
                    </span>
                    <h2 className="fw-bold mb-0" style={{ color: "#ffffff", fontSize: "2rem", letterSpacing: "-0.03em" }}>
                      {card.value ?? 0}
                    </h2>
                  </div>
                  <div className="rounded-4 d-flex align-items-center justify-content-center" style={{ width: "52px", height: "52px", backgroundColor: "rgba(255, 255, 255, 0.18)", backdropFilter: "blur(4px)", fontSize: "1.4rem" }}>
                    {card.icon}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Graphs Grid ── */}
        <div className="row g-4 mb-5">
          <div className="col-lg-4">
            <div className="card border-0 rounded-4 p-3 h-100" style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)" }}>
              <div className="card-body">
                <div className="mb-4">
                  <h6 className="fw-bold mb-1" style={{ color: "#1e293b" }}>💬 Messages / Day</h6>
                </div>
                <div style={{ height: "240px" }}><Line data={messagesGraph} options={chartOptions} /></div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card border-0 rounded-4 p-3 h-100" style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)" }}>
              <div className="card-body">
                <div className="mb-4">
                  <h6 className="fw-bold mb-1" style={{ color: "#1e293b" }}>🏢 Registrations / Day</h6>
                </div>
                <div style={{ height: "240px" }}><Bar data={businessGraph} options={chartOptions} /></div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card border-0 rounded-4 p-3 h-100" style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)" }}>
              <div className="card-body">
                <div className="mb-4">
                  <h6 className="fw-bold mb-1" style={{ color: "#1e293b" }}>📝 Posts / Day</h6>
                </div>
                <div style={{ height: "240px" }}><Line data={postsGraph} options={chartOptions} /></div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Data Matrices ── */}
        <div className="row g-4">
          
          {/* Top Businesses Table */}
          <div className="col-lg-6">
            <div className="card border-0 rounded-4 overflow-hidden h-100" style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)" }}>
              <div className="p-4 border-bottom border-light bg-white">
                <h6 className="fw-bold mb-0" style={{ color: "#1e293b" }}>🔥 Active Businesses</h6>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead style={{ backgroundColor: "#f8fafc" }}>
                    <tr>
                      <th className="px-4 py-3 border-0 text-muted fw-semibold uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>Business Name</th>
                      <th className="text-end px-4 py-3 border-0 text-muted fw-semibold uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>Activity Counters</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topBusinesses.length === 0 ? (
                      <tr>
                        <td colSpan="2" className="text-center py-5 text-muted small">No data</td>
                      </tr>
                    ) : (
                      topBusinesses.map((b, i) => (
                        <tr key={`${b.business_profile_id}-top-${i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td className="px-4 py-3 fw-semibold text-dark" style={{ fontSize: "0.9rem" }}>{b.business_name}</td>
                          <td className="text-end px-4 py-3">
                            <span className="badge bg-light text-primary border border-primary-subtle rounded-3 px-2.5 py-1.5 fw-bold me-2" style={{ fontSize: "0.75rem" }}>
                              {b.message_count} msgs
                            </span>
                            <span className="badge bg-light text-warning border border-warning-subtle rounded-3 px-2.5 py-1.5 fw-bold" style={{ fontSize: "0.75rem" }}>
                              {b.post_count} posts
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Recent Chats Table */}
          <div className="col-lg-6">
            <div className="card border-0 rounded-4 overflow-hidden h-100" style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)" }}>
              <div className="p-4 border-bottom border-light bg-white">
                <h6 className="fw-bold mb-0" style={{ color: "#1e293b" }}>💬 Recent Chats</h6>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead style={{ backgroundColor: "#f8fafc" }}>
                    <tr>
                      <th className="px-4 py-3 border-0 text-muted fw-semibold uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>Business Name</th>
                      <th className="text-end px-4 py-3 border-0 text-muted fw-semibold uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentChats.length === 0 ? (
                      <tr>
                        <td colSpan="2" className="text-center py-5 text-muted small">No data</td>
                      </tr>
                    ) : (
                      recentChats.map((c, i) => (
                        <tr key={`${c.chat_id}-chat-${i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td className="px-4 py-3 fw-semibold text-dark" style={{ fontSize: "0.9rem" }}>{c.business_name}</td>
                          <td className="text-end px-4 py-3">
                            <span className="badge bg-light text-success border border-success-subtle rounded-3 px-2.5 py-1.5 fw-bold" style={{ fontSize: "0.75rem" }}>
                              {c.message_count} msgs
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Recent Posts Full Width Table */}
          <div className="col-12">
            <div className="card border-0 rounded-4 overflow-hidden" style={{ backgroundColor: "#ffffff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)" }}>
              <div className="p-4 border-bottom border-light bg-white">
                <h6 className="fw-bold mb-0" style={{ color: "#1e293b" }}>📝 Recent Posts</h6>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead style={{ backgroundColor: "#f8fafc" }}>
                    <tr>
                      <th className="px-4 py-3 border-0 text-muted fw-semibold uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>Business Name</th>
                      <th className="text-end px-4 py-3 border-0 text-muted fw-semibold uppercase tracking-wider" style={{ fontSize: "0.75rem" }}>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPosts.length === 0 ? (
                      <tr>
                        <td colSpan="2" className="text-center py-5 text-muted small">No data</td>
                      </tr>
                    ) : (
                      recentPosts.map((p, i) => (
                        <tr key={`${p.post_id}-post-${i}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td className="px-4 py-3 fw-semibold text-dark" style={{ fontSize: "0.9rem" }}>{p.business_name}</td>
                          <td className="text-end px-4 py-3 text-secondary small" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {p.created_at}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;